/* WeQuote Dev Board — Priority List (PRD §6): Focus (master–detail) + Grid
 * tabs; filters (priority, developer, design-need, type/bug, due-date); sorts
 * incl. SUGGESTED (two bands + pins + blockers + apply/undo + explainer).
 * Faithful port of renderDetail/detailRowHtml/renderList + the suggest tooling.
 * Focus rows drag-to-reorder in Sort: Priority; the Suggested view refuses
 * drags (pin instead) exactly like the prototype. */
import React, { useRef, useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as M from '../core/mutations'
import * as S from '../core/suggest'
import type { Feature } from '../core/types'
import { DEVCOLS, DL, EL, isProgress, timeLabel, statusLabels, MODULE_OPTS, isPool } from '../core/constants'
import { rankCls, daysLeft } from '../core/dates'
import * as L from '../core/logic'
import { activePhase, statusDisplay, statusPillCls, phaseStripeCls, rankMap, inPipelineF, isDevOverdue, isDesignOverdue } from '../core/logic'
import { useQuickEdit } from './QuickEdit'
import { TitleWithBug, StChip, AssignedPill, DateLine, DesignLine, RequestLine } from './parts'
import { ReadView, EditView } from './CardModal'
import { IcFlag, IcPin, IcFilter, IcX, IcCalendar } from '../icons'
import { EditPanel } from './CardModal'

type Tab = 'detail' | 'list'

/* ── suggest chip (list variant, with nudge arrows) ── */
const SuggestChip: React.FC<{ feat: Feature; withNudge: boolean; sortMode: S.SortMode }> = ({ feat, withNudge, sortMode }) => {
  const { cards, openSubModal } = useDevBoard()
  if (!S.isTriage(feat)) return null
  let nudge: React.ReactNode = null
  if (withNudge && sortMode !== 'suggested') {
    const sr = S.suggestRankMap(cards)[feat.id]
    const cr = rankMap(cards)[feat.id]
    if (sr && cr) {
      if (sr <= cr - 3) nudge = <span className="sug-nudge up" title="Suggestion ranks this higher">▲</span>
      else if (sr >= cr + 3) nudge = <span className="sug-nudge down" title="Suggestion ranks this lower">▼</span>
    }
  }
  return (
    <>
      <span className="sug-chip" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'suggestInfo', id: feat.id }) }} title="Click to see where this would rank and why">Suggested {S.suggestScore(feat)} ⓘ</span>
      {feat.blocker && <span className="sug-blocker" title="Blocker — pinned to the top"><IcFlag size={11} /> Blocker</span>}
      {feat.triagePinned && <span className="sug-pin" title="Pinned position"><IcPin size={11} /></span>}
      {nudge}
    </>
  )
}

const PinControl: React.FC<{ feat: Feature; sortMode: S.SortMode }> = ({ feat, sortMode }) => {
  const { cards, mutate, openSubModal } = useDevBoard()
  if (sortMode !== 'suggested' || !S.isTriage(feat)) return null
  if (feat.blocker) return <span className="pinctl"><span className="pin-blk" title="Blocker — pinned to the top"><IcFlag size={11} /> Blocker</span></span>
  if (feat.triagePinned) {
    return (
      <span className="pinctl">
        <button className="pinbtn" title="Move pinned card up" onClick={(e) => { e.stopPropagation(); mutate((fs) => S.nudgePin(fs, feat.id, -1)) }}>▲</button>
        <button className="pinbtn" title="Move pinned card down" onClick={(e) => { e.stopPropagation(); mutate((fs) => S.nudgePin(fs, feat.id, 1)) }}>▼</button>
        <button className="pinbtn on" title="Pinned — click to unpin" onClick={(e) => { e.stopPropagation(); mutate((fs) => S.unpinCard(fs, feat.id)) }}><IcPin size={11} /> Pinned</button>
      </span>
    )
  }
  return <span className="pinctl"><button className="pinbtn" title="Pin this card’s position so it stays put as scores change" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'pinConfirm', id: feat.id, pos: S.pinDefaultPos(feat.id, cards) }) }}><IcPin size={11} /> Pin</button></span>
}

const BandHeader: React.FC<{ band: string }> = ({ band }) =>
  band === 'committed'
    ? <div className="band-hd"><span className="band-hd-t">Committed / scheduled</span><span className="band-hd-s">design complete, scheduled or in development — follows the roadmap</span></div>
    : band === 'design'
      ? <div className="band-hd band-design"><span className="band-hd-t">Design in progress</span><span className="band-hd-s">a designer is actively working on these</span></div>
      : band === 'now'
        ? <div className="band-hd band-now"><span className="band-hd-t">Now</span><span className="band-hd-s">next up — about to be picked up</span></div>
        : band === 'next'
          ? <div className="band-hd band-next"><span className="band-hd-t">Next</span><span className="band-hd-s">the shortlist — queued behind Now</span></div>
          : band === 'later'
            ? <div className="band-hd band-later"><span className="band-hd-t">Later</span><span className="band-hd-s">the raw pool — everything not yet shortlisted</span></div>
            : band === 'triage'
              ? <div className="band-hd"><span className="band-hd-t">Triage backlog</span><span className="band-hd-s">suggested order — pin a card to lock its place</span></div>
              : null

/* ── Advisory band hint (Lee, 2 Sep): from the suggested score — flags a
 * card that looks like it belongs in a different band. Advice only; the
 * human moves it (or doesn't). ── */
const BandHint: React.FC<{ feat: Feature }> = ({ feat }) => {
  if (!isPool(feat.status)) return null
  const hint = S.suggestBand(feat)
  if (hint === feat.status) return null
  const label = hint === 'now' ? 'Now' : hint === 'next' ? 'Next' : 'Later'
  const up = (hint === 'now') || (hint === 'next' && feat.status === 'later')
  return <span className={'band-hint' + (up ? ' up' : '')} title={'The suggested score says this looks like a ' + label + ' card — advisory only, nothing moves by itself.'}>{up ? '↑' : '↓'} looks like {label}</span>
}

/* ── Focus row (detailRowHtml) ── */
const FocusRow: React.FC<{ feat: Feature; rank: number; selected: boolean; sortMode: S.SortMode; onClick: () => void }> = ({ feat, rank, selected, sortMode, onClick }) => {
  const { openSubModal } = useDevBoard()
  const ph = activePhase(feat)
  const over = isDevOverdue(feat) || isDesignOverdue(feat) ? ' overdue' : ''
  const dev = feat.dev && DL[feat.dev] ? DL[feat.dev] : 'Unassigned'
  const qe = useQuickEdit(feat) // click a field on the row to edit it in place
  return (
    <div className={'dv-row ' + phaseStripeCls(feat) + (selected ? ' selected' : '') + over} onClick={onClick}>
      {qe.editor}
      <div className="dv-row-top">
        <span className="dv-drag" title="Drag to reorder">⠇</span>
        <span className={'pnum-badge ' + rankCls(rank)}>{rank}</span>
        <span className="dv-row-title"><TitleWithBug feat={feat} /></span>
        {feat.time && (feat.multiStage
          ? <span className={'badge effort-' + feat.eff} title="Effort derives from the stage estimates">{EL[feat.eff]}</span>
          : <span className={'badge effort-' + feat.eff + ' qe-click'} title="Click to change the estimate" onClick={qe.start('time')}>{EL[feat.eff]}</span>)}
        <span className="dv-row-sch"><button className="sched-mini" title="Schedule — pick a dev and start date" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'schedule', id: feat.id }) }}><IcCalendar size={11} /> Schedule</button></span>
      </div>
      <div className="dv-row-sub">
        <span className={'status-pill ' + statusPillCls(feat) + ' qe-click'} title="Click to change status" onClick={qe.start('status')}>{statusDisplay(feat)}</span>
        <span className="dv-row-dev qe-text" title="Click to change the assigned dev" onClick={qe.start('dev')}>{dev}</span>
        <StChip feat={feat} />
        <SuggestChip feat={feat} withNudge sortMode={sortMode} />
        <BandHint feat={feat} />
        <PinControl feat={feat} sortMode={sortMode} />
      </div>
      {ph === 'dev' && (
        <div className="dv-row-bar"><div className="pbar"><div className={'pbar-fill' + (feat.status === 'onhold' ? ' hold' : '')} style={{ width: (feat.progress || 0) + '%' }} /></div><span className="dv-row-pct">{feat.progress || 0}% complete</span></div>
      )}
      {ph === 'design' && (
        <div className="dv-row-dbar"><div className="pbar dbar"><div className="pbar-fill dfill" style={{ width: (feat.designProgress || 0) + '%' }} /></div><span className="dv-row-pct">{feat.designProgress || 0}% designed</span></div>
      )}
      <RequestLine feat={feat} />
    </div>
  )
}

/* ── Grid card (renderList item) ── */
const GridCard: React.FC<{ feat: Feature; rank: number; sortMode: S.SortMode }> = ({ feat, rank, sortMode }) => {
  const { mutate, openCardModal } = useDevBoard()
  const [open, setOpen] = useState(false)
  const prog = isProgress(feat.status)
  const dl = feat.status === 'onhold' ? { txt: 'ON HOLD', cls: 'red' } : daysLeft(feat.deadline)
  const qe = useQuickEdit(feat) // click a field to edit it in place
  return (
    <div className={'list-item ' + phaseStripeCls(feat) + (open ? ' open' : '')}>
      {qe.editor}
      <div className="list-row" onClick={() => setOpen((v) => !v)}>
        <div className="card-head">
          <div className="ch-left">
            <span className={'pnum-badge ' + rankCls(rank)}>{rank}</span>
            <span className="row-title"><TitleWithBug feat={feat} /></span>
          </div>
          <div className="ch-right">{feat.time && <span className={'badge effort-' + feat.eff + ' qe-click'} title="Click to change the estimate" onClick={qe.start('time')}>{EL[feat.eff]}</span>}</div>
        </div>
        <div className="lc-body">
          <div className="lc-left">
            {prog ? (
              <>
                <div className="est-sm">Est {feat.time ? timeLabel(feat.time) : 'TBD'}</div>
                <div className="pbar"><div className={'pbar-fill' + (feat.status === 'onhold' ? ' hold' : '')} style={{ width: (feat.progress || 0) + '%' }} /></div>
                <div className="pmeta"><span className="pct">{feat.progress || 0}%</span><span className={'dleft ' + dl.cls}>{dl.txt}</span></div>
                <DateLine feat={feat} />
              </>
            ) : (
              <>
                <div className="notes-trunc">{feat.notes}</div>
                <div className="est-plain">Est {feat.time ? timeLabel(feat.time) : 'TBD'}</div>
                {activePhase(feat) === 'design' && (
                  <div className="pv-dbar"><div className="pbar dbar"><div className="pbar-fill dfill" style={{ width: (feat.designProgress || 0) + '%' }} /></div><span className="dv-row-pct">{feat.designProgress || 0}% designed</span></div>
                )}
                {activePhase(feat) === 'design' && <DesignLine feat={feat} />}
                <DateLine feat={feat} />
                <RequestLine feat={feat} />
              </>
            )}
          </div>
          <div className="lc-right">
            {prog ? (
              <>
                <StChip feat={feat} />
                <AssignedPill dev={feat.dev} />
                {feat.status === 'testing' && <span className="status-pill s-testing">Ready to Test</span>}
                {feat.status === 'testcomplete' && <span className="status-pill s-testcomplete">Testing Complete</span>}
                {feat.status === 'onhold' && <button className="face-resume" onClick={(e) => { e.stopPropagation(); mutate((fs) => M.resumeCard(fs, feat.id)) }}>Resume</button>}
                {feat.pnotes
                  ? <div className="pnote-trunc">{feat.pnotes}</div>
                  : <span className="pnote-add" onClick={(e) => { e.stopPropagation(); openCardModal(feat.id) }}>Add progress note</span>}
              </>
            ) : (
              <>
                <StChip feat={feat} />
                <SuggestChip feat={feat} withNudge sortMode={sortMode} />
                <PinControl feat={feat} sortMode={sortMode} />
                <span className="assigned-text">{feat.dev && DL[feat.dev] ? 'Assigned to: ' + DL[feat.dev] : 'Unassigned'}</span>
                <span className={'status-pill ' + statusPillCls(feat)}>{statusDisplay(feat)}</span>
              </>
            )}
          </div>
        </div>
      </div>
      {open && <div className="list-expand" style={{ display: 'block' }} onClick={(e) => e.stopPropagation()}><EditPanel feat={feat} /></div>}
    </div>
  )
}

/* ── the view ── */
export const PriorityView: React.FC<{ tab: Tab; setTab: (t: Tab) => void }> = ({ tab, setTab }) => {
  const { cards, mutate, showToast, openSubModal, openCardModal, pendingFocusCard, consumeFocusCard, focusFilter, setFocusFilterManual, reqDatalists } = useDevBoard()
  const [pri, setPri] = useState('all')
  const [dev, setDev] = useState('all')
  const [design, setDesign] = useState('all')
  const [statusF, setStatusF] = useState('all')
  const [reqBy, setReqBy] = useState('all')
  const [modulesF, setModulesF] = useState<string[]>([]) // multi-select (Lee, 28 Aug)
  const [bandF, setBandF] = useState('all') // Now/Next/Later band filter (v2 §1)
  const toggleModule = (mo: string) => setModulesF((cur) => cur.indexOf(mo) > -1 ? cur.filter((x) => x !== mo) : [...cur, mo])
  const [filtersOpen, setFiltersOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('wq-devboard-prifilters') === 'open' } catch { return false }
  })
  const toggleFilters = () => setFiltersOpen((v) => {
    try { localStorage.setItem('wq-devboard-prifilters', v ? 'closed' : 'open') } catch { /* convenience only */ }
    return !v
  })
  const [bug, setBug] = useState('all')
  const [sortMode, setSortMode] = useState<S.SortMode>('priority')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  /* Lee, 28 Aug: closing a card must not lose your place — the list reflows
   * when the detail pane opens/closes, so we re-centre the card's row on
   * open AND on close. */
  const lastSelRef = React.useRef<number | null>(null)
  React.useEffect(() => {
    const centre = (id: number) => {
      requestAnimationFrame(() => {
        const el = document.querySelector('.wq-devboard .dv-list [data-cardid="' + id + '"]')
        if (el) (el as HTMLElement).scrollIntoView({ block: 'center' })
      })
    }
    if (selectedId != null) { lastSelRef.current = selectedId; centre(selectedId) }
    else if (lastSelRef.current != null) { centre(lastSelRef.current) }
  }, [selectedId])
  const [inlineEdit, setInlineEdit] = useState(false)

  React.useEffect(() => {
    if (pendingFocusCard != null) { setSelectedId(pendingFocusCard); setInlineEdit(false); consumeFocusCard() }
  }, [pendingFocusCard, consumeFocusCard])
  const dragId = useRef<number | null>(null)
  const [dragOverId, setDragOverId] = useState<number | null>(null)
  const undoRef = useRef<{ id: number; po: number }[] | null>(null)

  const pipe = cards.filter(inPipelineF)
  const items = S.applySort(pipe.filter(S.toolbarFilter({ pri, dev, design, bug, status: statusF, reqBy, modules: modulesF, band: bandF })), sortMode).filter(S.focusFilterFn(focusFilter))
  const rmap = rankMap(cards)
  const n = items.length
  const selFeat = selectedId != null ? cards.find((f) => f.id === selectedId) || null : null
  const sug = sortMode === 'suggested'

  const apply = () => {
    const { result, undo } = S.applySuggested(cards)
    undoRef.current = undo
    mutate(() => result)
    setSortMode('priority')
    showToast('Suggested layout applied — Undo is in the toolbar.')
  }
  const organise = () => {
    const r = S.organisePool(cards)
    if ('toast' in r) { showToast(r.toast); return }
    undoRef.current = r.undo
    mutate(() => r.result)
    showToast('Pool organised by score — blockers first. Undo is in the toolbar.')
  }
  const undoApply = () => {
    if (!undoRef.current) return
    const u = undoRef.current
    undoRef.current = null
    mutate((fs) => S.undoApplySuggested(fs, u))
  }
  const onDrop = (targetId: number) => {
    const from = dragId.current
    dragId.current = null
    setDragOverId(null)
    if (from == null || from === targetId) return
    if (sug) { showToast('Cards can’t be rearranged in the Suggested view — use a card’s Pin to lock its place, or switch to Sort: Priority to reorder by hand.'); return }
    mutate((fs) => S.reorder(fs, from, targetId))
  }

  /* side-panel filters: active chips + count (Lee, 28 Aug) */
  const SL_ = statusLabels()
  const reqByLabel = reqBy === 'all' ? '' : (reqBy[0] === 'c' ? reqBy.slice(2) : reqBy.slice(2) + ' (person)')
  const chips: { key: string; label: string; clear: () => void }[] = []
  if (pri !== 'all') chips.push({ key: 'pri', label: pri === 'high' ? 'High priority' : pri === 'medium' ? 'Medium priority' : 'Low priority', clear: () => setPri('all') })
  if (statusF !== 'all') chips.push({ key: 'status', label: SL_[statusF] || statusF, clear: () => setStatusF('all') })
  if (dev !== 'all') chips.push({ key: 'dev', label: 'Dev: ' + (DL[dev] || dev), clear: () => setDev('all') })
  if (design !== 'all') chips.push({ key: 'design', label: design === 'yes' ? 'Design required' : 'No design required', clear: () => setDesign('all') })
  if (bug !== 'all') chips.push({ key: 'bug', label: bug === 'bugs' ? 'Bugs only' : 'Bugs hidden', clear: () => setBug('all') })
  if (focusFilter !== 'all') chips.push({ key: 'due', label: ({ overdue: 'Overdue', duesoon: 'Due within 7 days', 'design-overdue': 'Design overdue', 'dev-overdue': 'Development overdue', 'design-soon': 'Design due, dev soon' } as Record<string, string>)[focusFilter] || focusFilter, clear: () => setFocusFilterManual('all') })
  if (bandF !== 'all') chips.push({ key: 'band', label: bandF === 'now' ? 'Now' : bandF === 'next' ? 'Next' : 'Later', clear: () => setBandF('all') })
  modulesF.forEach((mo) => chips.push({ key: 'mod:' + mo, label: mo, clear: () => toggleModule(mo) }))
  if (reqBy !== 'all') chips.push({ key: 'reqby', label: 'Requested by ' + reqByLabel, clear: () => setReqBy('all') })
  const activeCount = chips.length
  const clearAll = () => { setBandF('all'); setModulesF([]); setPri('all'); setStatusF('all'); setDev('all'); setDesign('all'); setBug('all'); setFocusFilterManual('all'); setReqBy('all') }

  /* band boundaries for the suggested view */
  let lastBand = ''

  return (
    <>
      <div className="toolbar">
        <button className={'reorder-btn pf-toggle' + (filtersOpen ? ' active' : '')} title={filtersOpen ? 'Hide filters' : 'Show filters'} onClick={toggleFilters}>
          <IcFilter size={13} /> Filters{activeCount > 0 ? <span className="pf-cnt">{activeCount}</span> : null}
        </button>
        {chips.map((c) => (
          <span key={c.key} className="pf-chip">{c.label}<b title="Clear this filter" onClick={c.clear}><IcX size={11} /></b></span>
        ))}
        <span className="sep" />
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value as S.SortMode)}>
          <option value="priority">Sort: Priority</option>
          <option value="suggested">Sort: Suggested</option>
          <option value="requests">Sort: Most requested</option>
          <option value="quickwins">Sort: Quick wins first</option>
          <option value="effort">Sort: Lowest effort first</option>
        </select>
        <button className="reorder-btn" title="Re-sort each band by suggested score (blockers first) — undoable" onClick={organise}>Organise pool</button>
        {sug && <button className="reorder-btn" onClick={() => openSubModal({ kind: 'resetSuggest' })}>Remove all pinned and reset suggestions</button>}
        {sug && <button className="reorder-btn active" onClick={apply}>Apply suggested layout</button>}
        {undoRef.current && !sug && <button className="reorder-btn" onClick={undoApply}>Undo suggested layout</button>}
        <div className="right-tools"><span className="cnt">{n} feature{n === 1 ? '' : 's'}</span></div>
      </div>

      <div className="pf-layout">
      <div className="pf-main">
      <div id="priorityTabs">
        <div className="view-toggle">
          <button className={'vbtn' + (tab === 'detail' ? ' active' : '')} onClick={() => setTab('detail')}>Focus</button>
          <button className={'vbtn' + (tab === 'list' ? ' active' : '')} onClick={() => setTab('list')}>Grid</button>
        </div>
      </div>

      {sug && (
        <div className="sug-bar" style={{ margin: '10px 24px 0' }}>
          <span className="sug-bar-txt">Ordered by a suggested score (priority + demand + effort). Pin a card to lock its place; mark a blocker to force it to the top; <b>Apply</b> to save.</span>
          <a className="sug-bar-link" onClick={() => openSubModal({ kind: 'suggestHelp' })}>How this works</a>
          <span className="sug-bar-counts">{pipe.filter(S.isCommitted).length} in progress · {pipe.filter(S.isTriage).length} suggested · {pipe.filter((f) => S.isTriage(f) && (f.triagePinned || f.blocker)).length} pinned</span>
        </div>
      )}

      {tab === 'detail' ? (
        <div id="detailView">
          <div className={'dv-wrap' + (selFeat ? ' has-selection' : '')}>
            <div className="dv-list">
              {items.length === 0 ? (
                <div className="dv-empty-list">No features match the current filters.</div>
              ) : (
                items.map((feat) => {
                  // v2 §1: section headers in BOTH sorts — Committed → Design → Now → Next → Later
                  const band = (sug || sortMode === 'priority') ? L.sectionOf(feat) : ''
                  const showBand = band !== '' && band !== lastBand && band !== 'other'
                  if (showBand) lastBand = band
                  return (
                    <React.Fragment key={feat.id}>
                      {showBand && <BandHeader band={band} />}
                      <div
                        draggable
                        data-cardid={feat.id}
                        className={dragOverId === feat.id ? 'drag-over' : ''}
                        onDragStart={() => { dragId.current = feat.id; if (sug) showToast('Cards can’t be rearranged in the Suggested view — use a card’s Pin to lock its place, or switch to Sort: Priority to reorder by hand.') }}
                        onDragOver={(e) => { if (dragId.current != null) { e.preventDefault(); setDragOverId(feat.id) } }}
                        onDragLeave={() => setDragOverId((c) => (c === feat.id ? null : c))}
                        onDrop={(e) => { e.preventDefault(); onDrop(feat.id) }}
                        onDragEnd={() => { dragId.current = null; setDragOverId(null) }}
                      >
                        <FocusRow feat={feat} rank={rmap[feat.id] || 0} selected={feat.id === selectedId} sortMode={sortMode} onClick={() => { setSelectedId((cur) => (cur === feat.id ? null : feat.id)); setInlineEdit(false) }} />
                      </div>
                    </React.Fragment>
                  )
                })
              )}
            </div>
            <div className="dv-detail">
              {selFeat && (inlineEdit
                ? <EditView feat={selFeat} onToggleEdit={() => setInlineEdit(false)} onClose={() => { setSelectedId(null); setInlineEdit(false) }} />
                : <ReadView feat={selFeat} inline onToggleEdit={() => setInlineEdit(true)} onClose={() => { setSelectedId(null); setInlineEdit(false) }} />)}
            </div>
          </div>
        </div>
      ) : (
        <div id="listView">
          <div className="list-wrap">
            <p className="list-hint">Click any card to expand and edit — or open the full pop-up from a row</p>
            <div className="list-cols" id="listCols">
              <div className="list-col">{items.slice(0, Math.ceil(items.length / 2)).map((f) => <GridCard key={f.id} feat={f} rank={rmap[f.id] || 0} sortMode={sortMode} />)}</div>
              <div className="list-col">{items.slice(Math.ceil(items.length / 2)).map((f) => <GridCard key={f.id} feat={f} rank={rmap[f.id] || 0} sortMode={sortMode} />)}</div>
            </div>
          </div>
        </div>
      )}
      </div>
      {filtersOpen && (
        <aside className="pf-panel">
          <div className="pf-hd">
            <span className="pf-title">Filters</span>
            {activeCount > 0 && <button className="pf-clear" onClick={clearAll}>Clear all</button>}
            <button className="pf-x" title="Hide filters" onClick={toggleFilters}><IcX size={15} /></button>
          </div>
          <div className="pf-body">
            {/* Rule (Lee, 28 Aug): more than 3 options → a dropdown, not a
                radio pile — keeps the panel short. Radios only for ≤3. */}
            <div className="pf-sec"><div className="pf-sec-hd">Priority</div>
              {[['all', 'Any'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']].map(([v, l]) => (
                <label key={v} className="pf-opt"><input type="radio" name="pf-pri" checked={pri === v} onChange={() => setPri(v)} />{l}</label>
              ))}
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Band</div>
              {[['all', 'Any'], ['now', 'Now'], ['next', 'Next'], ['later', 'Later']].map(([v, l]) => (
                <label key={v} className="pf-opt"><input type="radio" name="pf-band" checked={bandF === v} onChange={() => setBandF(v)} />{l}</label>
              ))}
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Status</div>
              <select className="pf-select" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
                <option value="all">Any</option>
                {Object.entries(SL_).filter(([k]) => !['needsreview', 'live', 'archived', 'idea'].includes(k)).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Developer</div>
              <select className="pf-select" value={dev} onChange={(e) => setDev(e.target.value)}>
                <option value="all">Any</option>
                {DEVCOLS.map((d) => <option key={d} value={d}>{DL[d]}</option>)}
              </select>
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Module <span className="pf-multi">(pick several)</span></div>
              <select className="pf-select" value="" onChange={(e) => { if (e.target.value) toggleModule(e.target.value === '__none__' ? '' : e.target.value) }}>
                <option value="">Add a module…</option>
                {MODULE_OPTS.filter((mo) => modulesF.indexOf(mo) === -1).map((mo) => <option key={mo} value={mo}>{mo}</option>)}
                {modulesF.indexOf('') === -1 && <option value="__none__">No module (imported)</option>}
              </select>
              {modulesF.length > 0 && (
                <div className="pf-modchips">
                  {modulesF.map((mo) => (
                    <span key={mo || 'none'} className="pf-chip">{mo || 'No module'}<b title="Remove" onClick={() => toggleModule(mo)}><IcX size={11} /></b></span>
                  ))}
                </div>
              )}
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Requested by</div>
              <select className="pf-select" value={reqBy} onChange={(e) => setReqBy(e.target.value)}>
                <option value="all">Any</option>
                <optgroup label="Companies">
                  {reqDatalists.companies.map((c) => <option key={'c' + c} value={'c:' + c}>{c}</option>)}
                </optgroup>
                <optgroup label="People">
                  {reqDatalists.people.map((c) => <option key={'p' + c} value={'p:' + c}>{c}</option>)}
                </optgroup>
              </select>
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Design</div>
              {[['all', 'Any'], ['yes', 'Design required'], ['no', 'No design required']].map(([v, l]) => (
                <label key={v} className="pf-opt"><input type="radio" name="pf-des" checked={design === v} onChange={() => setDesign(v)} />{l}</label>
              ))}
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Type</div>
              {[['all', 'Any'], ['bugs', 'Bugs only'], ['nobugs', 'Hide bugs']].map(([v, l]) => (
                <label key={v} className="pf-opt"><input type="radio" name="pf-bug" checked={bug === v} onChange={() => setBug(v)} />{l}</label>
              ))}
            </div>
            <div className="pf-sec"><div className="pf-sec-hd">Due date</div>
              <select className="pf-select" value={focusFilter} onChange={(e) => setFocusFilterManual(e.target.value as S.FocusFilter)}>
                <option value="all">Any</option>
                <option value="overdue">Overdue</option>
                <option value="duesoon">Due within 7 days</option>
                <option value="design-overdue">Design overdue</option>
                <option value="dev-overdue">Development overdue</option>
                <option value="design-soon">Design due, dev soon</option>
              </select>
            </div>
          </div>
        </aside>
      )}
      </div>
    </>
  )
}
